import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, tap} from 'rxjs';
import {environment} from 'src/environments/environment';
import {SecurityUtils} from 'src/app/services/auth/security.utils';
import {CurrentTenantStore} from 'src/app/services/tenant/current-tenant.store';
import {
	ApplicationAutodiscover,
	ApplicationAutodiscoverCount,
	ApplicationAutodiscoverStatusForm,
	AutodiscoverWorkstation
} from 'src/app/services/applications/autodiscover.model';

@Injectable({providedIn: 'root'})
export class ApplicationAutodiscoverService {

	private httpClient = inject(HttpClient);
	private tenantStore = inject(CurrentTenantStore);

	serviceUrl = (tenantId: string) => `${environment.engineURL}/tenants/${tenantId}/applications/autodiscover`;

	getAllApplicationAutodiscover(tenantId: string): Observable<ApplicationAutodiscover[]> {
		return this.httpClient.get<ApplicationAutodiscover[]>(`${this.serviceUrl(tenantId)}`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	/**
	 * One discovery as the inbox shows it, or null (empty response) when the application is not in the inbox — it is an
	 * inventory application. Errors are left to the caller (403: no inbox for this tenant or user).
	 */
	getApplicationAutodiscover(tenantId: string, applicationId: string): Observable<ApplicationAutodiscover | null> {
		return this.httpClient.get<ApplicationAutodiscover | null>(`${this.serviceUrl(tenantId)}/${encodeURIComponent(applicationId)}`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	/**
	 * Pending-discoveries count for the badge. Postgres-only on the engine side: use it wherever only the
	 * number matters, the full inbox (`getAllApplicationAutodiscover`) reads usage and teams from Elasticsearch.
	 */
	getApplicationAutodiscoverCount(tenantId: string): Observable<ApplicationAutodiscoverCount> {
		return this.httpClient.get<ApplicationAutodiscoverCount>(`${this.serviceUrl(tenantId)}/_count`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getAutodiscoverWorkstations(tenantId: string, autodiscoverId: string): Observable<AutodiscoverWorkstation[]> {
		return this.httpClient.get<AutodiscoverWorkstation[]>(`${this.serviceUrl(tenantId)}/${autodiscoverId}/workstations`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	updateApplicationAutodiscoverStatus(tenantId: string, autodiscoverId: string, form: ApplicationAutodiscoverStatusForm): Observable<boolean> {
		return this.httpClient.post<boolean>(`${this.serviceUrl(tenantId)}/${autodiscoverId}`, form, {
			headers: SecurityUtils.getAuthHttpHeaders()
		}).pipe(
			// Triaging moves the app in or out of the inventory: keep the app count
			// (page header + main menu) live, like new-application.service mutations.
			tap(success => {
				if (success) this.tenantStore.refreshApplicationCount();
			})
		);
	}
}
